import React, { useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Receipt } from 'lucide-react';
import {
  useBookings,
  useClientOutstandingOrders,
} from '../../../../hooks/accommodation';
import { useAppSelector } from '../../../../hooks/useRedux';
import { selectAccommodationProperty } from '../../../../store/accommodation';
import { queryKeys } from '../../../../lib/queryKeys';
import { SettleCreditBillModal } from '../../../sales/SettleCreditBillModal';
import { ClientOutstandingOrder } from '../../../../services/accommodationService';

export const OutstandingRestaurantBillsCard: React.FC = () => {
  const { bookingId } = useParams<{ bookingId: string }>();
  const property = useAppSelector(selectAccommodationProperty);
  const { data: bookings = [] } = useBookings(property?.id);
  const booking = bookings.find((b) => b.id === bookingId) ?? null;

  const clientId = booking?.client?.id ?? null;
  const { data: outstandingOrders = [], isLoading: isLoadingOutstanding } =
    useClientOutstandingOrders(clientId);

  const [settlingOrder, setSettlingOrder] =
    useState<ClientOutstandingOrder | null>(null);

  const queryClient = useQueryClient();
  const handleCreditBillSettled = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.accommodation.outstandingOrders(clientId ?? ''),
    });
  }, [queryClient, clientId]);

  if (!booking || !booking.client) return null;

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-5 space-y-3.5">
      <div className="flex items-center justify-between border-b border-gray-100 pb-2">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          Outstanding Restaurant Bills
        </h4>
        {outstandingOrders.length > 0 && (
          <span className="rounded-full bg-red-50 px-2.5 py-0.5 text-xs font-semibold text-red-500">
            {outstandingOrders
              .reduce((s, o) => s + o.amount, 0)
              .toLocaleString()}{' '}
            due
          </span>
        )}
      </div>

      {isLoadingOutstanding ? (
        <p className="text-xs text-gray-400 font-medium">Loading…</p>
      ) : outstandingOrders.length === 0 ? (
        <div className="flex items-center gap-2 rounded-xl bg-green-50 px-3 py-2.5 text-xs text-green-700 font-medium">
          <Receipt size={14} className="shrink-0" />
          No outstanding restaurant bills for this guest.
        </div>
      ) : (
        <div className="space-y-2">
          {outstandingOrders.map((order) => (
            <div
              key={order.id}
              className="flex items-center justify-between rounded-xl border border-gray-100 px-3.5 py-3 bg-slate-50/50"
            >
              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold text-gray-800">
                  Bill #{order.bill_number}
                  {order.table_name ? ` · ${order.table_name}` : ''}
                  {order.section_name ? ` (${order.section_name})` : ''}
                </p>
                <p className="text-xs text-gray-400 font-medium mt-0.5">
                  {new Date(order.sale_date).toLocaleDateString(undefined, {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                  })}
                  {order.notes ? ` · ${order.notes}` : ''}
                </p>
              </div>
              <div className="ml-3 flex items-center gap-3 shrink-0">
                <span className="text-sm font-bold text-red-500">
                  {order.amount.toLocaleString()}
                </span>
                <button
                  type="button"
                  onClick={() => setSettlingOrder(order)}
                  className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 transition-colors"
                >
                  Pay Now
                </button>
              </div>
            </div>
          ))}

          {/* Total outstanding */}
          <div className="flex items-center justify-between rounded-xl bg-red-50 px-3.5 py-2 text-xs font-bold text-red-600">
            <span>Total outstanding</span>
            <span>
              {outstandingOrders
                .reduce((s, o) => s + o.amount, 0)
                .toLocaleString()}
            </span>
          </div>
        </div>
      )}

      <SettleCreditBillModal
        isOpen={!!settlingOrder}
        order={settlingOrder}
        onClose={() => setSettlingOrder(null)}
        onSettled={handleCreditBillSettled}
      />
    </div>
  );
};
